import { useEffect, useState } from 'react';
// A demo window is 7 days; urgency escalates as it closes.
const NOTICE_THRESHOLD_S = 48 * 3600;
const URGENT_THRESHOLD_S = 24 * 3600;
const CRITICAL_THRESHOLD_S = 6 * 3600;
const tierForSeconds = (s) => {
    if (s <= 0)
        return 'expired';
    if (s <= CRITICAL_THRESHOLD_S)
        return 'critical';
    if (s <= URGENT_THRESHOLD_S)
        return 'urgent';
    if (s <= NOTICE_THRESHOLD_S)
        return 'notice';
    return 'calm';
};
// Human, unambiguous label. Coarsens with time-remaining so it stays compact:
//   >= 72h  -> days + hours      ("6d 23h")
//   <  72h  -> hours + minutes   ("47h 30m", "15m", "<1m")
// Avoids the old decimal-hours form ("3.25h") and the absurd "167h 58m".
const labelForSeconds = (s) => {
    if (s <= 0)
        return 'Expired';
    const totalMinutes = Math.floor(s / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    if (totalHours >= 72) {
        const d = Math.floor(totalHours / 24);
        const h = totalHours % 24;
        return `${d}d ${h}h`;
    }
    const m = totalMinutes % 60;
    if (totalHours > 0)
        return `${totalHours}h ${m}m`;
    if (m > 0)
        return `${m}m`;
    return '<1m';
};
// Seconds-level clock for the unlock modal's live countdown, e.g.
// "3h 15m 42s", "15m 42s", "42s".
export const formatClock = (s) => {
    if (s <= 0)
        return 'Expired';
    const total = Math.floor(s);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    if (h > 0)
        return `${h}h ${m}m ${sec}s`;
    if (m > 0)
        return `${m}m ${sec}s`;
    return `${sec}s`;
};
// Per-tier presentation, shared by every countdown surface.
export const DEMO_TIER_COLORS = {
    calm: '#06b6d4', // cyan — matches the HD Demo badge
    notice: '#f59e0b', // amber
    urgent: '#f97316', // orange
    critical: '#dc2626', // red
    expired: '#dc2626', // red
};
// "Hot" tiers get bold, high-contrast text (never thin/spindly).
export const demoTierIsHot = (tier) => tier === 'urgent' || tier === 'critical' || tier === 'expired';
// The final stretch flashes to draw the eye.
export const demoTierShouldPulse = (tier) => tier === 'critical';
// The expired-demo line. Shared so every low-res surface — the live-countdown
// branch here and the server-tagged locked branch in UserProjectItem — reads
// identically instead of drifting into two different strings.
export const DEMO_EXPIRED_LINE = 'Your demo project has expired. Unlock now.';
// Centralized copy so the list item, project header, and banner stay in sync.
// Primary line leads with the $100 discount (the real, already-true lever).
export const demoPrimaryLine = (c) => {
    switch (c.tier) {
        case 'expired':
            return DEMO_EXPIRED_LINE;
        case 'critical':
        case 'urgent':
            return `Only ${c.label} left for your demo project. Unlock now.`;
        case 'notice':
            return `${c.label} left for your demo project. Unlock now.`;
        case 'calm':
        default:
            return `${c.label} left on your demo project. Click to unlock.`;
    }
};
// Secondary, stacked line: what the demo state becomes after expiry.
export const DEMO_SECONDARY_LINE = "Then it's $549 and your view drops to low resolution.";
export function useDemoCountdown(demoExpiresAt, active, 
// Default ticks once a minute (list/header). Pass 1000 for a per-second clock
// (the unlock modal's live countdown).
tickMs = 60000) {
    const [countdown, setCountdown] = useState(null);
    useEffect(() => {
        if (!demoExpiresAt || !active) {
            setCountdown(null);
            return;
        }
        const update = () => {
            const remaining = demoExpiresAt - Date.now() / 1000; // seconds
            setCountdown({
                tier: tierForSeconds(remaining),
                secondsRemaining: Math.max(0, remaining),
                label: labelForSeconds(remaining),
            });
        };
        update();
        const interval = setInterval(update, tickMs);
        return () => clearInterval(interval);
    }, [demoExpiresAt, active, tickMs]);
    return countdown;
}
